import { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Image } from 'expo-image';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, type Href } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ArrowRight, User, Linkedin, BadgeCheck } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import { OnboardingLayout, STARTUP_ONBOARDING_DISPLAY_STEPS, INVITEE_ONBOARDING_STEPS } from '@/components/OnboardingLayout';
import { OnboardingSignOut } from '@/components/OnboardingSignOut';
import { useAuth } from '@/lib/supabase/auth';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { capitalizeWords } from '@/lib/format';
import { handleError, reportError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';
import { buildUserProfilePrefill } from '@/lib/onboarding/user-profile-prefill';
import {
  FOUNDER_STATUS_OPTIONS,
  canCreateOrClaim,
  type FounderStatus,
} from '@/lib/onboarding/founder-status';
import { validatePersonalFields, FUNCTION_OPTIONS, FUNCTION_OTHER, type PersonalFields } from '@/lib/onboarding/personal-fields';
import { nextStep } from '@/lib/onboarding/steps';
import { persistOnboardingStep } from '@/lib/onboarding/persist-step';
import { track } from '@/lib/analytics';
import { useEntrance } from '@/lib/a11y/reduced-motion';
import AnimatedPressable from '@/components/AnimatedPressable';

const ERROR_MESSAGES: Record<string, string> = {
  firstName: 'First name is required',
  lastName: 'Last name is required',
  founderStatus: 'Please select your founder status',
  functionTitle: 'Please select your function',
};

/**
 * Split a persisted function title into the picklist selection + custom text.
 * A value matching a preset (other than "Other") selects that card; anything
 * else (a previously-typed custom title) selects "Other" and prefills the
 * free-text field. Empty stays unselected (no silent default).
 */
function splitFunctionTitle(persisted: string): { choice: string; other: string } {
  if (!persisted) return { choice: '', other: '' };
  if (FUNCTION_OPTIONS.includes(persisted) && persisted !== FUNCTION_OTHER) {
    return { choice: persisted, other: '' };
  }
  return { choice: FUNCTION_OTHER, other: persisted };
}

/** Row shape read off the caller's own `team_members` membership, if any. */
interface OwnMembershipRow {
  id: string;
  founder_status: FounderStatus | null;
  function_title: string | null;
}

/**
 * Resolves whether the signed-in user already has a `team_members` row
 * (e.g. they arrived via the pre-existing `team-accept-invite` flow, which
 * inserts an ACTIVE membership immediately on invite acceptance — before
 * onboarding is ever reached). If they do, this IS the invite path: their
 * founder status was decided by whoever invited them and is shown read-only
 * here rather than asked, and `nextStep('personal', true)` skips straight to
 * `complete` (they already have a company).
 *
 * This is a best-effort heuristic, not an authoritative "isInvitee" signal —
 * `src/app/index.tsx`'s router guard has the same documented gap ("this
 * screen doesn't load team_members, so we can't yet tell... corrected once
 * membership resolves"). A brand-new founder has no row yet (`data` is
 * `null`), which is the common case this file must get right.
 */
function useOwnMembership(userId: string | undefined) {
  return useQuery({
    queryKey: ['onboarding', 'own-membership', userId],
    queryFn: async (): Promise<OwnMembershipRow | null> => {
      const { data, error } = await supabase
        .from('team_members')
        .select('id, founder_status, function_title')
        .eq('user_id', userId as string)
        .neq('status', 'deactivated')
        .maybeSingle();
      if (error) throw error;
      return data as OwnMembershipRow | null;
    },
    enabled: isSupabaseConfigured() && !!userId,
  });
}

export default function StartupPersonalScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { isDesktop } = useResponsive();
  const user = useProfileStore((s) => s.user);
  const setUser = useProfileStore((s) => s.setUser);
  const { error: showError } = useAlert();
  const entrance = useEntrance();

  // Get LinkedIn data + the real profile/auth-user rows from Supabase auth
  const { getLinkedInData, profile: authProfile, user: authUser, refreshProfile } = useAuth();
  const linkedInData = getLinkedInData();

  // T1.1 — pure prefill (name/email always; LinkedIn avatar/headline/company
  // when the user signed up via LinkedIn). A previously-confirmed
  // profiles.first_name/last_name wins over the raw LinkedIn name.
  const prefill = useMemo(
    () => buildUserProfilePrefill(authProfile, authUser),
    [authProfile, authUser]
  );

  const { data: membership } = useOwnMembership(authUser?.id);
  const isInvitee = !!membership;

  // Progress UI source (Task 5): an invitee is in the canonical machine's
  // 2-step invited_member flow (invite → personal), so their progress reads
  // "Step 2 of 2" from `INVITEE_ONBOARDING_STEPS` — never the 4-step founder
  // list. A self-identified founder keeps the full founder progress. NOTE:
  // navigation still resolves the NEXT route from `STARTUP_ONBOARDING_STEPS`
  // below (it carries the shared `complete` bookend that the machine's
  // invited_member flow deliberately omits) — this variable only drives the
  // progress dots + "Step X of Y" label.
  const progressSteps = isInvitee ? INVITEE_ONBOARDING_STEPS : STARTUP_ONBOARDING_DISPLAY_STEPS;
  const mobileStepIndex = progressSteps.findIndex((s) => s.id === 'personal');

  // Final-review IMPORTANT 3: a self-identified founder (non-invitee) may
  // only pick founder|co_founder here — 'team_member' is dropped from the
  // SELECTABLE control so they can't funnel into the retired self-service
  // join path (startup-company.tsx no longer offers Join at all). The
  // invitee flow keeps the full list: their founder status is preset
  // read-only from the inviter's choice (below) and may legitimately be
  // 'team_member'.
  const selectableFounderStatusOptions = isInvitee
    ? FOUNDER_STATUS_OPTIONS
    : FOUNDER_STATUS_OPTIONS.filter((opt) => opt.value !== 'team_member');

  const [firstName, setFirstName] = useState(prefill.firstName);
  const [lastName, setLastName] = useState(prefill.lastName);
  // Derived, not stateful: `avatarUrl` has no user-edit path anywhere in this
  // screen (it's display-only, sourced from LinkedIn) — it only ever tracked
  // `prefill.avatarUrl`, so it's computed directly instead of mirrored into
  // useState + a backfill effect.
  const avatarUrl = useMemo(() => prefill.avatarUrl ?? null, [prefill.avatarUrl]);
  const existingProgress = (authProfile?.onboarding_progress as Record<string, unknown> | null) || null;
  // No preselect (Task 3): a self-identified founder must actively choose
  // founder|co-founder — a defaulted 'founder' silently pre-decided ownership.
  // Resumes to a previously-saved value if one exists.
  const [founderStatus, setFounderStatus] = useState<FounderStatus | null>(
    () => (existingProgress?.founder_status_detail as FounderStatus | undefined) ?? null
  );
  // Function is a picklist (CEO/CTO/…/Other) where "Other" reveals a free-text
  // field. Track the selected card and the custom text separately; the
  // effective title (below) resolves them, so a persisted custom value resumes
  // correctly.
  const initialFunction = splitFunctionTitle((existingProgress?.function_title as string | undefined) || '');
  const [functionChoice, setFunctionChoice] = useState<string>(initialFunction.choice);
  const [functionOther, setFunctionOther] = useState<string>(initialFunction.other);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Derived, not stateful: same reasoning as `avatarUrl` above — no
  // user-edit path, it only ever tracked `prefill`/`linkedInData`.
  const prefilledFromLinkedIn = useMemo(
    () => linkedInData.isLinkedInAuth || !!prefill.avatarUrl || !!prefill.headline,
    [linkedInData.isLinkedInAuth, prefill.avatarUrl, prefill.headline]
  );

  const emailVerified = !!authUser?.email_confirmed_at;

  // Backfill once the profile/auth-user load resolves after mount (e.g. right
  // after an OAuth redirect) — never clobber a value the user already edited.
  // Only firstName/lastName are backfilled here: avatarUrl/prefilledFromLinkedIn
  // are derived above and update automatically when `prefill` changes.
  useEffect(() => {
    if (!firstName && prefill.firstName) setFirstName(prefill.firstName);
    if (!lastName && prefill.lastName) setLastName(prefill.lastName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill.firstName, prefill.lastName]);

  // The invite path pre-sets founder status from the membership row rather
  // than asking — a null value on that row (the existing team-accept-invite
  // flow never wrote founder_status; it only distinguishes admin/member)
  // falls back to 'team_member', the conservative non-owner default for an
  // accepted generic invite. Also backfills function_title if it was already
  // set (e.g. resuming after a prior partial save).
  useEffect(() => {
    if (!membership) return;
    setFounderStatus(membership.founder_status || 'team_member');
    if (membership.function_title) {
      const split = splitFunctionTitle(membership.function_title);
      setFunctionChoice(split.choice);
      setFunctionOther(split.other);
    }
  }, [membership]);

  // Resolve the picklist selection + custom text into one concrete title.
  const functionTitle = functionChoice === FUNCTION_OTHER ? functionOther.trim() : functionChoice;

  const handleContinue = async () => {
    const fields: PersonalFields = {
      firstName,
      lastName,
      founderStatus: founderStatus as FounderStatus,
      functionTitle,
    };
    const errorKeys = validatePersonalFields(fields, { requireFounderStatus: true });
    if (errorKeys.length > 0) {
      const newErrors: Record<string, string> = {};
      errorKeys.forEach((key) => {
        newErrors[key] = ERROR_MESSAGES[key] || 'This field is required';
      });
      setErrors(newErrors);
      return;
    }
    // Validation guarantees this on the founder path; narrow for the writes below.
    if (!founderStatus) return;
    if (isSaving) return;

    setIsSaving(true);
    try {
      const finalFirstName = capitalizeWords(firstName.trim());
      const finalLastName = capitalizeWords(lastName.trim());

      // Save to local (Zustand) state immediately so the rest of the app has it.
      setUser?.({
        ...user,
        name: [finalFirstName, finalLastName].filter(Boolean).join(' '),
        email: prefill.email,
        role: functionTitle ? functionTitle.toLowerCase() : user?.role,
        avatarUrl: avatarUrl || undefined,
        linkedIn: linkedInData.linkedInUrl || undefined,
      });

      // Real persistence — no simulate.
      if (isSupabaseConfigured() && authUser?.id) {
        const profileUpdate: Record<string, unknown> = {
          first_name: finalFirstName || null,
          last_name: finalLastName || null,
          onboarding_progress: {
            ...(existingProgress || {}),
            // Legacy binary flag — still read by startup-company.tsx's claim
            // gate and the onboarding-claim edge function. Keep writing it
            // until those are migrated to read team_members.founder_status
            // directly (team_members has no row yet at this step for a
            // brand-new founder — the team is created later, at the company
            // step — so it cannot be the write target for that path today).
            founder_status: canCreateOrClaim(founderStatus) ? 'founder_or_cofounder' : 'member',
            // Granular value this task introduces — becomes the source of
            // truth once the company-step/claim edge functions are migrated.
            founder_status_detail: founderStatus,
            function_title: functionTitle || null,
          },
        };

        // LinkedIn verify flag — set once we actually have a LinkedIn
        // identity backing the prefill (OAuth signup), not merely that the
        // banner is showing.
        if (linkedInData.isLinkedInAuth && linkedInData.linkedInUrl) {
          profileUpdate.linkedin_verified = true;
          profileUpdate.linkedin_verified_at = new Date().toISOString();
          profileUpdate.linkedin_url = linkedInData.linkedInUrl;
        }

        const { error } = await supabase.from('profiles').update(profileUpdate).eq('id', authUser.id);
        if (error) throw error;
        await refreshProfile();

        // Invite path: a team_members row already exists for this user —
        // persist the real values there too (it's the eventual source of
        // truth). Non-fatal: the profiles-side write above already
        // succeeded, so a hiccup here must not strand the user.
        if (membership?.id) {
          const { error: memberError } = await supabase
            .from('team_members')
            .update({ founder_status: founderStatus, function_title: functionTitle || null })
            .eq('id', membership.id);
          if (memberError) {
            reportError(memberError, {
              context: 'StartupPersonalScreen.handleContinue.teamMember',
              level: 'warning',
            });
          }
        }

        const next = nextStep('personal', isInvitee);
        if (next) await persistOnboardingStep(authUser.id, next);
      }

      // Activation funnel (proposal §8) — fired ONLY after the awaited profile
      // write above succeeds (a throw skips to catch, so this never marks a
      // failed save as done). The invite path is a distinct funnel from the
      // founder path even though they share this screen.
      track(isInvitee ? 'member_personal_done' : 'founder_personal_done');

      // Navigate to the next step (company anchor for a new founder, or
      // straight to complete for an invitee who already has a company).
      const nextId = nextStep('personal', isInvitee);
      const nextPath = STARTUP_ONBOARDING_DISPLAY_STEPS.find((s) => s.id === nextId)?.path;
      router.push((nextPath || '/(onboarding)/startup-company') as Href);
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'StartupPersonalScreen.handleContinue' });
      if (!isCancelled) showError('Could not save your profile', message);
    } finally {
      setIsSaving(false);
    }
  };

  // Form content (shared between mobile and desktop)
  const formContent = (
    <>
      <View className={isWeb && isDesktop ? 'gap-5' : 'gap-4'}>
        {/* LinkedIn Pre-fill Banner */}
        {prefilledFromLinkedIn && (
          <View
            className="flex-row items-center gap-2 p-3 rounded-xl overflow-hidden mb-2"
            style={{ backgroundColor: '#E7F3FF' }}
          >
            <Linkedin size={18} color="#0A66C2" />
            <Text numberOfLines={2} style={{ color: '#0A66C2', fontSize: 13, flex: 1 }}>
              Your profile has been pre-filled with your LinkedIn data
              {(prefill.headline || prefill.currentCompany)
                ? ` — ${[prefill.headline, prefill.currentCompany].filter(Boolean).join(' · ')}`
                : ''}
            </Text>
          </View>
        )}

        {/* Profile Picture from LinkedIn */}
        {avatarUrl && (
          <View className="items-center mb-4">
            <View
              className="w-24 h-24 rounded-full overflow-hidden"
              style={{ borderWidth: 3, borderColor: colors.primary[500] }}
            >
              <Image
                source={{ uri: avatarUrl }}
                style={{ width: '100%', height: '100%' }}
                contentFit="cover"
              />
            </View>
            <Text style={{ color: colors.text.tertiary }} className="text-xs mt-2">
              Profile photo from LinkedIn
            </Text>
          </View>
        )}

        {/* First / Last Name */}
        <View className="flex-row gap-3">
          <View className="flex-1">
            <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
              First Name <Text style={{ color: colors.red.main }}>*</Text>
            </Text>
            <TextInput
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: errors.firstName ? colors.red.main : colors.border.light,
                borderRadius: 12,
                padding: 16,
                color: colors.text.primary,
                fontSize: 16,
              }}
              placeholder="Ada"
              placeholderTextColor={colors.text.tertiary}
              value={firstName}
              onChangeText={(text) => {
                setFirstName(text);
                if (errors.firstName) setErrors((e) => ({ ...e, firstName: '' }));
              }}
              autoCapitalize="words"
              accessibilityLabel="First name"
              accessibilityHint={errors.firstName || undefined}
            />
            {errors.firstName && (
              <Text
                style={{ color: colors.red.main }}
                className="text-xs mt-1"
                numberOfLines={2}
                accessibilityRole="alert"
                accessibilityLiveRegion="polite"
              >
                {errors.firstName}
              </Text>
            )}
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
              Last Name <Text style={{ color: colors.red.main }}>*</Text>
            </Text>
            <TextInput
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: errors.lastName ? colors.red.main : colors.border.light,
                borderRadius: 12,
                padding: 16,
                color: colors.text.primary,
                fontSize: 16,
              }}
              placeholder="de la Cruz"
              placeholderTextColor={colors.text.tertiary}
              value={lastName}
              onChangeText={(text) => {
                setLastName(text);
                if (errors.lastName) setErrors((e) => ({ ...e, lastName: '' }));
              }}
              autoCapitalize="words"
              accessibilityLabel="Last name"
              accessibilityHint={errors.lastName || undefined}
            />
            {errors.lastName && (
              <Text
                style={{ color: colors.red.main }}
                className="text-xs mt-1"
                numberOfLines={2}
                accessibilityRole="alert"
                accessibilityLiveRegion="polite"
              >
                {errors.lastName}
              </Text>
            )}
          </View>
        </View>

        {/* Email — read-only, already verified at signup */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Email
          </Text>
          <View
            className="flex-row items-center justify-between rounded-xl px-4"
            style={{
              backgroundColor: colors.surface.secondary,
              borderWidth: 1,
              borderColor: colors.border.light,
              paddingVertical: 16,
            }}
          >
            <Text
              numberOfLines={1}
              style={{ color: colors.text.secondary, fontSize: 16, flex: 1 }}
            >
              {prefill.email}
            </Text>
            {emailVerified && (
              <View className="flex-row items-center gap-1 ml-2">
                <BadgeCheck size={16} color={colors.success} />
                <Text style={{ color: colors.success }} className="text-xs font-medium">
                  Verified
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Founder status */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Founder Status <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {selectableFounderStatusOptions.map((opt) => {
              const selected = founderStatus === opt.value;
              return (
                <AnimatedPressable
                  key={opt.value}
                  onPress={() => {
                    if (isInvitee) return;
                    setFounderStatus(opt.value);
                    if (errors.founderStatus) setErrors((e) => ({ ...e, founderStatus: '' }));
                  }}
                  disabled={isInvitee}
                  accessibilityRole="radio"
                  accessibilityLabel={opt.label}
                  accessibilityState={{ selected, disabled: isInvitee }}
                  className="px-4 py-3 rounded-xl"
                  style={{
                    minHeight: 44,
                    justifyContent: 'center',
                    backgroundColor: selected ? colors.primary[600] : colors.surface.primary,
                    borderWidth: 1,
                    borderColor: selected ? colors.primary[600] : colors.border.light,
                    opacity: isInvitee && !selected ? 0.5 : 1,
                  }}
                >
                  <Text
                    numberOfLines={1}
                    style={{
                      color: selected ? colors.text.inverse : colors.text.primary,
                      fontWeight: '500',
                    }}
                  >
                    {opt.label}
                  </Text>
                </AnimatedPressable>
              );
            })}
          </View>
          {isInvitee && (
            <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">
              Set by your invite — contact your team owner to change it.
            </Text>
          )}
          {errors.founderStatus && (
            <Text
              style={{ color: colors.red.main }}
              className="text-xs mt-1"
              accessibilityRole="alert"
              accessibilityLiveRegion="polite"
            >
              {errors.founderStatus}
            </Text>
          )}
        </View>

        {/* Function / title */}
        <View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
            Function <Text style={{ color: colors.red.main }}>*</Text>
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {FUNCTION_OPTIONS.map((option) => {
              const selected = functionChoice === option;
              return (
                <AnimatedPressable
                  key={option}
                  onPress={() => {
                    setFunctionChoice(option);
                    if (errors.functionTitle) setErrors((e) => ({ ...e, functionTitle: '' }));
                  }}
                  accessibilityRole="radio"
                  accessibilityLabel={option}
                  accessibilityState={{ selected }}
                  className="px-4 py-3 rounded-xl"
                  style={{
                    minHeight: 44,
                    justifyContent: 'center',
                    backgroundColor: selected ? colors.primary[600] : colors.surface.primary,
                    borderWidth: 1,
                    borderColor: selected ? colors.primary[600] : colors.border.light,
                  }}
                >
                  <Text
                    numberOfLines={1}
                    style={{
                      color: selected ? colors.text.inverse : colors.text.primary,
                      fontWeight: '500',
                    }}
                  >
                    {option}
                  </Text>
                </AnimatedPressable>
              );
            })}
          </View>
          {functionChoice === FUNCTION_OTHER && (
            <TextInput
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: errors.functionTitle ? colors.red.main : colors.border.light,
                borderRadius: 12,
                padding: 16,
                color: colors.text.primary,
                fontSize: 16,
                marginTop: 8,
              }}
              placeholder="e.g., Head of Growth"
              placeholderTextColor={colors.text.tertiary}
              value={functionOther}
              onChangeText={(text) => {
                setFunctionOther(text);
                if (errors.functionTitle) setErrors((e) => ({ ...e, functionTitle: '' }));
              }}
              autoCapitalize="words"
              accessibilityLabel="Custom function title"
              accessibilityHint={errors.functionTitle || undefined}
            />
          )}
          {errors.functionTitle && (
            <Text
              style={{ color: colors.red.main }}
              className="text-xs mt-1"
              numberOfLines={2}
              accessibilityRole="alert"
              accessibilityLiveRegion="polite"
            >
              {errors.functionTitle}
            </Text>
          )}
        </View>

        {/* Info Note */}
        <View
          className="p-4 rounded-xl mt-2"
          style={{ backgroundColor: colors.primary[50] }}
        >
          <Text style={{ color: colors.primary[700] }} className="text-sm leading-relaxed">
            This information helps us personalize your experience. Your profile is separate from your company profile.
          </Text>
        </View>
      </View>

      {/* Continue Button */}
      <View className="mt-8">
        <AnimatedPressable
          onPress={handleContinue}
          disabled={isSaving}
          accessibilityRole="button"
          accessibilityLabel="Continue"
          accessibilityState={{ disabled: isSaving, busy: isSaving }}
          className="rounded-xl py-4 flex-row items-center justify-center gap-2"
          style={{ minHeight: 44, backgroundColor: colors.primary[600], opacity: isSaving ? 0.7 : 1 }}
        >
          <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
            {isSaving ? 'Saving...' : 'Continue'}
          </Text>
          {!isSaving && <ArrowRight size={20} color={colors.text.inverse} />}
        </AnimatedPressable>
      </View>
    </>
  );

  // Desktop layout with OnboardingLayout
  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        currentStep="personal"
        steps={progressSteps}
        title="Your Information"
        subtitle="Tell us about yourself"
        userType="startup"
        entityName={[firstName, lastName].filter(Boolean).join(' ') || undefined}
      >
        {formContent}
      </OnboardingLayout>
    );
  }

  // Mobile layout
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          className="flex-1"
        >
          {/* Header */}
          <Animated.View
            entering={entrance(FadeInDown.delay(100).duration(500))}
            className="px-5 pt-4 pb-4"
          >
            <View className="flex-row items-center justify-between">
              <Pressable
                onPress={() => {
                  // Personal is the first onboarding step now that welcome is
                  // removed — guard so a back-press on the first screen is a
                  // safe no-op instead of an unhandled GO_BACK.
                  if (router.canGoBack()) router.back();
                }}
                accessibilityRole="button"
                accessibilityLabel="Go back"
                className="w-11 h-11 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <View className="flex-1 flex-row gap-2 mx-3">
                {progressSteps.map((step, index) => (
                  <View
                    key={step.id}
                    className="h-1 flex-1 rounded-full"
                    style={{
                      backgroundColor: index <= mobileStepIndex ? colors.primary[500] : colors.border.light,
                      maxWidth: 40,
                    }}
                  />
                ))}
              </View>
              <OnboardingSignOut />
            </View>
          </Animated.View>

          {/* Title */}
          <Animated.View
            entering={entrance(FadeInDown.delay(200).duration(500))}
            className="px-5 pb-6"
          >
            <View className="flex-row items-center gap-3 mb-2">
              <User size={24} color={colors.primary[600]} />
              <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                Step {mobileStepIndex + 1} of {progressSteps.length}
              </Text>
            </View>
            <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
              Your Information
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
              Tell us about yourself
            </Text>
          </Animated.View>

          {/* Form */}
          <ScrollView
            className="flex-1 px-5"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <Animated.View entering={entrance(FadeInUp.delay(300).duration(500))} className="pb-8">
              {formContent}
            </Animated.View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}
